import { useEffect, useImperativeHandle, useRef, type Ref } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import { markdownExtensions } from "./markdownExtensions";
import { SlashMenu } from "./SlashMenu";
import { TableToolbar } from "./TableToolbar";
import { useTranslation } from "react-i18next";

export type MarkdownEditorHandle = { focus: () => void };

/** Edit formatted content while keeping the API value as Markdown. */
export function MarkdownEditor({
  value,
  onChange,
  onSave,
  saveOnBlur = false,
  placeholder = "",
  autoFocus = false,
  disabled = false,
  minRows = 3,
  variant = "default",
  projectId,
  onBusyChange,
  ref,
}: {
  value: string;
  onChange: (value: string) => void;
  onSave?: (value: string) => void;
  saveOnBlur?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  minRows?: number;
  variant?: "default" | "plain";
  projectId: string;
  onBusyChange?: (editorBusy: boolean) => void;
  ref?: Ref<MarkdownEditorHandle>;
}) {
  const { t } = useTranslation();
  const lastValue = useRef(value);
  const editorBusy = useRef(false);
  const editor = useEditor({
    extensions: markdownExtensions(`${placeholder} ${t("editor.placeholder")}`.trim(), false, t("editor.placeholder")),
    // MarkdownPaste parses regular pastes; keep Shift-paste completely literal.
    enablePasteRules: false,
    content: value,
    contentType: "markdown",
    editable: !disabled,
    autofocus: autoFocus ? "end" : false,
    editorProps: {
      attributes: {
        class: `prose-tern tern-editor w-full outline-none focus-visible:outline-none ${variant === "plain" ? "py-1" : "px-4 py-3"}`,
        role: "textbox",
        "aria-label": placeholder,
        "aria-multiline": "true",
        "aria-disabled": String(disabled),
        style: `min-height: ${Math.max(80, minRows * 28)}px`,
      },
      handleKeyDown: (_view, event) => {
        if (event.isComposing || event.keyCode === 229) return false;
        if ((onSave || editorBusy.current) && (event.metaKey || event.ctrlKey) && event.key === "Enter") {
          event.preventDefault();
          event.stopPropagation();
          if (!editorBusy.current) onSave?.(lastValue.current);
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor }) => {
      const markdown = editor.isEmpty ? "" : editor.getMarkdown();
      lastValue.current = markdown;
      onChange(markdown);
    },
    onBlur: ({ event, editor }) => {
      if (event.relatedTarget instanceof Node && editor.view.dom.contains(event.relatedTarget)) return;
      if (saveOnBlur && !editorBusy.current) onSave?.(lastValue.current);
    },
  });

  useImperativeHandle(ref, () => ({ focus: () => { editor?.commands.focus(); } }), [editor]);

  useEffect(() => {
    if (!editor || value === lastValue.current) return;
    // Only replace external values (e.g. Create more / a posted comment), never
    // reparse each keystroke: that would reset the cursor, undo history, and IME.
    editor.commands.setContent(value, { contentType: "markdown", emitUpdate: false });
    lastValue.current = value;
  }, [editor, value]);

  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [editor, disabled]);

  return (
    <div className={variant === "plain" ? "min-w-0" : "min-w-0 rounded-lg border border-border-strong bg-bg transition-colors focus-within:border-accent"}>
      <TableToolbar editor={editor} />
      <EditorContent editor={editor} />
      <SlashMenu editor={editor} projectId={projectId} onBusyChange={(busy) => { editorBusy.current = busy; onBusyChange?.(busy); }} />
    </div>
  );
}
